/**
 * Cache Utility Module
 *
 * Provides localStorage-based caching with expiration support
 * for API responses to improve performance and reduce redundant requests.
 */

// Default cache duration: 5 minutes (in milliseconds)
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000

/**
 * Retrieves cached data from localStorage if it exists and hasn't expired
 *
 * @param {string} key - The cache key
 * @param {number} cacheDuration - Optional cache duration in milliseconds (default: 5 minutes)
 * @returns {any|null} The cached data or null if not found/expired
 */
export const getCachedData = (key, cacheDuration = DEFAULT_CACHE_DURATION) => {
  try {
    const cached = localStorage.getItem(key)

    if (!cached) {
      return null
    }

    const { data, timestamp } = JSON.parse(cached)
    const isExpired = Date.now() - timestamp > cacheDuration

    if (isExpired) {
      localStorage.removeItem(key)
      return null
    }

    return data
  } catch (error) {
    console.error(`Error retrieving cache for key "${key}":`, error)
    // If there's an error parsing, clear the corrupted cache
    localStorage.removeItem(key)
    return null
  }
}

/**
 * Stores data in localStorage with a timestamp
 *
 * @param {string} key - The cache key
 * @param {any} data - The data to cache
 * @returns {boolean} True if successful, false otherwise
 */
export const setCachedData = (key, data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(cacheObject))
    return true
  } catch (error) {
    console.error(`Error setting cache for key "${key}":`, error)
    // Handle QuotaExceededError - clear some old cache
    if (error.name === 'QuotaExceededError') {
      console.warn('LocalStorage quota exceeded. Consider clearing old cache.')
    }
    return false
  }
}

/**
 * Removes cached data for a specific key
 *
 * @param {string} key - The cache key to remove
 * @returns {boolean} True if successful, false otherwise
 */
export const clearCache = (key) => {
  try {
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.error(`Error clearing cache for key "${key}":`, error)
    return false
  }
}

/**
 * Clears all cache entries that match a pattern
 *
 * @param {string} pattern - A string pattern to match keys (e.g., "menu-")
 * @returns {number} The number of cache entries cleared
 */
export const clearCacheByPattern = (pattern) => {
  try {
    let clearedCount = 0
    const keys = Object.keys(localStorage)

    keys.forEach(key => {
      if (key.includes(pattern)) {
        localStorage.removeItem(key)
        clearedCount++
      }
    })

    return clearedCount
  } catch (error) {
    console.error(`Error clearing cache by pattern "${pattern}":`, error)
    return 0
  }
}

/**
 * Clears all expired cache entries
 *
 * @param {number} cacheDuration - Cache duration in milliseconds (default: 5 minutes)
 * @returns {number} The number of expired cache entries cleared
 */
export const clearExpiredCache = (cacheDuration = DEFAULT_CACHE_DURATION) => {
  try {
    let clearedCount = 0
    const keys = Object.keys(localStorage)

    keys.forEach(key => {
      try {
        const cached = localStorage.getItem(key)
        if (!cached) return

        const { timestamp } = JSON.parse(cached)
        const isExpired = Date.now() - timestamp > cacheDuration

        if (isExpired) {
          localStorage.removeItem(key)
          clearedCount++
        }
      } catch (error) {
        // If parsing fails, it's not a cache entry or corrupted
        // Skip or optionally remove
      }
    })

    return clearedCount
  } catch (error) {
    console.error('Error clearing expired cache:', error)
    return 0
  }
}

/**
 * Gets cache metadata (timestamp, age) without retrieving the full data
 *
 * @param {string} key - The cache key
 * @returns {object|null} Metadata object or null if not found
 */
export const getCacheMetadata = (key) => {
  try {
    const cached = localStorage.getItem(key)

    if (!cached) {
      return null
    }

    const { timestamp } = JSON.parse(cached)
    const age = Date.now() - timestamp

    return {
      timestamp,
      age,
      ageInMinutes: Math.floor(age / 60000),
      isExpired: (cacheDuration) => age > cacheDuration
    }
  } catch (error) {
    console.error(`Error retrieving cache metadata for key "${key}":`, error)
    return null
  }
}

/**
 * Cache key constants for the application
 */
export const CACHE_KEYS = {
  MENU_PRODUCTS: 'menu-products',
  MENU_SETS: 'menu-sets',
  MENU_INGREDIENTS: 'menu-ingredients',
  CAROUSEL: 'carousel',
  INSTAGRAM_IMAGES: 'instagram-images',
  DELIVERY_PRICES: 'delivery-prices',
  SYSTEM_STATUS: 'system-status'
}

/**
 * Cache duration constants (in milliseconds)
 */
export const CACHE_DURATIONS = {
  SHORT: 2 * 60 * 1000,      // 2 minutes
  MEDIUM: 5 * 60 * 1000,     // 5 minutes (default)
  LONG: 30 * 60 * 1000,      // 30 minutes
  VERY_LONG: 60 * 60 * 1000  // 1 hour
}
